/**
 * Utility functions for date and time formatting in Portuguese timezone
 */

/**
 * Format a system-generated timestamp (stored in UTC) to Portuguese timezone
 * @param {string|Date} dateString - The UTC date string from backend
 * @param {Object} options - Additional formatting options
 * @returns {string} Formatted date string in Portuguese timezone
 */
export const formatSystemDateTime = (dateString, options = {}) => {
  if (!dateString) return '';
  
  // Parse the date - backend sends UTC timestamps
  let date = new Date(dateString);
  
  // If the date string doesn't have timezone info, treat it as UTC
  if (typeof dateString === 'string' && !dateString.includes('Z') && !dateString.includes('+')) {
    date = new Date(dateString + 'Z');
  }
  
  const defaultOptions = {
    timeZone: 'Europe/Lisbon',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options
  };
  
  return date.toLocaleString('pt-PT', defaultOptions);
};

/**
 * Format a user-inputted timestamp (already in local time) to Portuguese locale
 * @param {string|Date} dateString - The date string as entered by user
 * @param {Object} options - Additional formatting options
 * @returns {string} Formatted date string in Portuguese locale (no timezone conversion)
 */
export const formatUserDateTime = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options
  };
  
  return new Date(dateString).toLocaleString('pt-PT', defaultOptions);
};

/**
 * Legacy function - use formatSystemDateTime or formatUserDateTime instead
 * @deprecated
 */
export const formatPortugueseDateTime = formatSystemDateTime;

/**
 * Format a date in Portuguese locale with Portugal timezone (date only)
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {string} Formatted date string in Portuguese timezone
 */
export const formatPortugueseDate = (dateString) => {
  return formatPortugueseDateTime(dateString, {
    hour: undefined,
    minute: undefined,
    second: undefined
  });
};

/**
 * Format a time in Portuguese locale with Portugal timezone (time only)
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {string} Formatted time string in Portuguese timezone
 */
export const formatPortugueseTime = (dateString) => {
  return formatPortugueseDateTime(dateString, {
    year: undefined,
    month: undefined,
    day: undefined
  });
};
